/**
 * WhatsApp relay resource — an agent links/verifies their OWN WhatsApp number so they can claim
 * and answer visitor chats from their phone (ARCHITECTURE §5).
 */

import type { HttpClient } from '../httpClient';
import { endpoints } from '../endpoints';
import type { WhatsAppLink, WhatsAppTeamMember } from '../../types';

export class WhatsAppRelayResource {
  constructor(private readonly http: HttpClient) {}

  /** The caller's linked WhatsApp numbers. */
  links(): Promise<WhatsAppLink[]> {
    return this.http.get(endpoints.whatsappRelay.links());
  }

  /** Owner/admin: all agents + their WhatsApp link. */
  adminTeam(): Promise<WhatsAppTeamMember[]> {
    return this.http.get(endpoints.whatsappRelay.adminTeam());
  }

  /** Owner/admin: set (link, trusted/verified) an agent's WhatsApp number. */
  adminSetLink(agentId: string, phoneE164: string): Promise<WhatsAppLink> {
    return this.http.put(endpoints.whatsappRelay.adminSetLink(agentId), { phoneE164 });
  }

  /** Owner/admin: remove an agent's WhatsApp number. */
  adminUnlink(agentId: string): Promise<void> {
    return this.http.delete(endpoints.whatsappRelay.adminSetLink(agentId));
  }

  /** Begin linking a number — sends a verification code to it over WhatsApp. */
  startLink(phoneE164: string): Promise<WhatsAppLink> {
    return this.http.post(endpoints.whatsappRelay.link(), { phoneE164 });
  }

  /** Confirm the code and activate the link. */
  verify(phoneE164: string, code: string): Promise<WhatsAppLink> {
    return this.http.post(endpoints.whatsappRelay.verify(), { phoneE164, code });
  }

  unlink(id: string): Promise<void> {
    return this.http.delete(endpoints.whatsappRelay.unlink(id));
  }
}
